import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import NumericInputWithArrows from './NumericInputWithArrows';

const schema = yup
	.array()
	.of(
		yup.object().shape({
			code: yup.string().required(),
			codeParent: yup.string().nullable(),
			number: yup.string().nullable(),
			nameOfficial: yup.string().nullable(),
			nameInternal: yup.string().nullable(),
			fraction: yup.number().nullable().min(0).max(100),
			children: yup
				.array()
				.of(
					yup.object().shape({
						code: yup.string().required(),
						codeParent: yup.string().required(),
						number: yup.string().nullable(),
						nameOfficial: yup.string().nullable(),
						nameInternal: yup.string().nullable(),
						fraction: yup.number().nullable().min(0).max(100),
					})
				)
				.nullable(),
		})
	)
	.test('validate-total-fraction', 'INVALID_TOTAL', function (items) {
		const totalFraction = items
			.filter((item) => item.codeParent === null)
			.reduce((sum, item) => sum + (item.fraction || 0), 0);

		return totalFraction <= 100;
	})
	.test('validate-children-fraction', function (items) {
		const errors = [];

		const itemMap = items.reduce((map, item) => {
			map[item.code] = item;
			return map;
		}, {});

		items.forEach((item) => {
			if (item.codeParent === null && item.children) {
				const parentFraction = item.fraction || 0;
				const childrenTotalFraction = item.children.reduce(
					(sum, child) => sum + (child.fraction || 0),
					0
				);

				if (childrenTotalFraction > parentFraction) {
					errors.push(`INVALID_CHILD_FRACTION_${item.code}`);
				}
			}
		});

		if (errors.length > 0) {
			return this.createError({
				message: errors.join(', '),
				path: 'children',
			});
		}

		return true;
	});

const WasteTypeInput = ({ categories, formField, isValid }) => {
	const { t } = useTranslation();
	const [categoryFractions, setCategoryFractions] = useState([]);
	const [errors, setErrors] = useState(null);

	useEffect(() => {
		if (formField.value && categories) {
			handleCategories();
		} else {
			setCategoryFractions(categories);
		}
	}, [categories, formField.value]);

	const handleCategories = () => {
		const _categories = categories.map((category) => {
			const savedCategory = formField.value.find((data) =>
				data?.type?.code
					? data?.type?.code === category.code
					: data.code === category.code
			);

			return savedCategory
				? { ...category, fraction: savedCategory.fraction }
				: { ...category };
		});

		formatCategories(_categories);
	};

	const formatCategories = (categories) => {
		const map = new Map();
		categories.forEach((item) => map.set(item.code, { ...item, children: [] }));

		const _categories = [];
		categories.forEach((item) => {
			if (item.codeParent) {
				const parent = map.get(item.codeParent);
				if (parent) {
					parent.children.push(map.get(item.code));
				}
			} else {
				_categories.push(map.get(item.code));
			}
		});

		setCategoryFractions(_categories);
	};

	useEffect(() => {
		isValid(!errors ? true : false);
	}, [errors]);

	const handleInputChange = (code, value) => {
		let _categories = categoryFractions?.map((item) => {
			const _item = { ...item };
			if (_item.code == code) {
				_item.fraction = value;
			} else {
				_item.children?.map((itemChild) => {
					if (itemChild.code == code) {
						itemChild.fraction = value;
					} else {
						return itemChild;
					}
				});
			}
			return _item;
		});
		setCategoryFractions(_categories);

		let _formValues = [];
		let _formCategories = [..._categories];
		_formCategories?.map((item) => {
			if (item.fraction > 0) {
				const _children = item.children;
				if (_children?.length > 0) {
					const selectedChildren = [];
					_children.map((child) => {
						if (child.fraction > 0) {
							selectedChildren.push(child);
						}
					});
					if (selectedChildren.length > 0) {
						_formValues = [..._formValues, item, ...selectedChildren];
					} else {
						_formValues = [..._formValues, item];
					}
				} else {
					_formValues.push(item);
				}
			}
		});
		const _values = [];
		_formValues.map(({ code, fraction }) => {
			_values.push({
				code,
				fraction,
			});
		});
		formField.setValue(_values);
		schema
			.validate(_categories, { abortEarly: false })
			.then(function (valid) {
				setErrors(null);
			})
			.catch(function (err) {
				const _errors = err.errors.reduce((result, item) => {
					return result.concat(item.split(',').map((str) => str.trim()));
				}, []);
				setErrors(_errors);
			});
	};

	return (
		<>
			<div className="input-fraction-label">
				<span>{t('waste_type')}</span>
				<span>{t('waste_fraction')}</span>
			</div>

			{categoryFractions.map((category) => (
				<div key={category.code}>
					{!category.codeParent && (
						<NumericInputWithArrows
							item={category}
							label={category.nameInternal}
							handleInputChange={({ code, fraction }) =>
								handleInputChange(code, fraction)
							}
							errors={errors?.includes('INVALID_TOTAL')}
						/>
					)}
					{category.fraction &&
						category.fraction > 0 &&
						category?.children?.map((subcategory) => (
							<NumericInputWithArrows
								key={subcategory.code}
								item={subcategory}
								label={subcategory.nameInternal}
								className="input-fraction input-fraction-child"
								handleInputChange={({ code, fraction }) =>
									handleInputChange(code, fraction)
								}
								errors={errors?.includes(
									'INVALID_CHILD_FRACTION_' + category.code
								)}
							/>
						))}
				</div>
			))}
		</>
	);
};

export default WasteTypeInput;
